/**
 * ContentFocus × DeliveryModality helpers for the Inclusive New Scene chooser.
 *
 * Config shape matches `content_focus_delivery_modality.yaml` /
 * `cfg:client:scenario_os:v1:scene_type_chooser_config`.
 */

/** Static fallback when GET /coach/scene-type-chooser-config is unavailable. */
export const FALLBACK_CHOOSER_CONFIG = {
  version: 1,
  content_focus: [
    {
      id: 'knowledge_analysis',
      label: 'Knowledge & Analysis',
      description:
        "Learners encounter, process, and analyse information. Bloom's: Remember, Understand, Analyse.",
      icon: '🔍',
      compatible_scene_types: [
        'inquiry_lab',
        'step_problem',
        'close_reading',
        'content_panel',
        'video',
        'slide_deck',
        'passage_question_set',
        'interactive_quiz',
        'flashcard_scene',
      ],
    },
    {
      id: 'procedure_application',
      label: 'Procedure & Application',
      description:
        "Learners execute, apply, or practise a skill or process. Bloom's: Apply, Synthesise (procedural).",
      icon: '⚙️',
      compatible_scene_types: [
        'checklist_procedure',
        'guided_example',
        'code_lab',
        'ai_lab',
        'puzzle',
        'phaser_2d',
        'three_3d',
      ],
    },
    {
      id: 'narrative_synthesis',
      label: 'Narrative & Synthesis',
      description:
        "Learners reason through ambiguous situations, make decisions, and construct meaning from a story arc. Bloom's: Evaluate, Create.",
      icon: '📖',
      compatible_scene_types: [
        'story_lab',
        'reflection',
        'capstone',
        'project',
        'whiteboard',
      ],
    },
  ],
  delivery_modality: [
    {
      id: 'self_directed',
      label: 'Self-Directed Exploration',
      description: 'Learner sets their own path through an open-ended challenge. No single correct sequence.',
      icon: '🧭',
      compatible_scene_types: ['inquiry_lab', 'ai_lab', 'curated_resource', 'prompt_canvas'],
    },
    {
      id: 'coach_directed',
      label: 'Coach-Directed Walkthrough',
      description:
        'Coach controls the sequence; each step is revealed after the learner completes the previous one.',
      icon: '🎯',
      compatible_scene_types: [
        'step_problem',
        'interactive_quiz',
        'puzzle',
        'phaser_2d',
        'three_3d',
      ],
    },
    {
      id: 'text_primary',
      label: 'Text-Primary Reading',
      description:
        'A written passage, document, or case study is the primary artifact. Comprehension tools surround the text.',
      icon: '📄',
      compatible_scene_types: ['close_reading', 'content_panel', 'passage_question_set'],
    },
    {
      id: 'checklist_primary',
      label: 'Checklist / Procedure Gate',
      description:
        'Completion is tracked through a step-by-step checklist. Each step must be acknowledged before the next unlocks.',
      icon: '✅',
      compatible_scene_types: ['checklist_procedure'],
    },
    {
      id: 'story_primary',
      label: 'Story & Branching Narrative',
      description: "A narrative unfolds across chapters; the learner's decisions determine the outcome.",
      icon: '🌿',
      compatible_scene_types: ['story_lab'],
    },
    {
      id: 'example_primary',
      label: 'Worked-Example Reference',
      description:
        'An expert solves a problem step by step; the learner observes, annotates, and saves for later reference. No graded response.',
      icon: '💡',
      compatible_scene_types: ['guided_example', 'video', 'slide_deck'],
    },
    {
      id: 'media_primary',
      label: 'Media / Presentation',
      description:
        'Video, slides, or embedded media carry the primary instructional load with optional overlay interactions.',
      icon: '🎬',
      compatible_scene_types: ['video', 'slide_deck', 'whiteboard'],
    },
  ],
};

/**
 * Normalize API / config_store payloads into `{ version, content_focus, delivery_modality }`.
 */
export function normalizeChooserConfig(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const cfg =
    raw.scene_type_chooser_config && typeof raw.scene_type_chooser_config === 'object'
      ? raw.scene_type_chooser_config
      : raw;
  const contentFocus = cfg.content_focus || cfg.contentFocus;
  const deliveryModality = cfg.delivery_modality || cfg.deliveryModality;
  if (!Array.isArray(contentFocus) || !Array.isArray(deliveryModality)) return null;
  return {
    version: cfg.version || 1,
    content_focus: contentFocus,
    delivery_modality: deliveryModality,
  };
}

function intersectIds(a, b) {
  if (!a) return b ? [...b] : [];
  if (!b) return [...a];
  const setB = new Set(b);
  return a.filter((id) => setB.has(id));
}

/**
 * Resolve compatible scene type ids from focus + modality picks.
 *
 * @param {object} config — normalized chooser config
 * @param {string|null} focusId
 * @param {string|null} modalityId
 * @param {boolean} browseAll — when true, return null (caller shows full domain list)
 * @returns {string[]|null} compatible ids, or null for "all"
 */
export function compatibleSceneTypeIds(config, focusId, modalityId, browseAll = false) {
  if (browseAll || (!focusId && !modalityId)) return null;

  const focus = (config?.content_focus || []).find((f) => f.id === focusId);
  const modality = (config?.delivery_modality || []).find((m) => m.id === modalityId);

  const focusIds = focus?.compatible_scene_types || null;
  const modalityIds = modality?.compatible_scene_types || null;

  if (focusId && !focus) return [];
  if (modalityId && !modality) return [];

  return intersectIds(focusIds, modalityIds);
}

/**
 * Filter domain scene-type catalog rows by chooser picks.
 *
 * @param {Array<{id: string}>} sceneTypes — domain allowlist / catalog
 * @param {string[]|null} compatibleIds — from compatibleSceneTypeIds; null = all
 */
export function filterSceneTypes(sceneTypes, compatibleIds) {
  const list = Array.isArray(sceneTypes) ? sceneTypes : [];
  if (compatibleIds == null) return list;
  const allowed = new Set(compatibleIds);
  return list.filter((t) => t && allowed.has(t.id));
}

/**
 * Drop focus/modality options that have zero overlap with the domain catalog.
 */
export function optionsWithDomainOverlap(options, sceneTypes) {
  const domainIds = new Set((sceneTypes || []).map((t) => t.id).filter(Boolean));
  return (options || []).filter((opt) => {
    const ids = opt.compatible_scene_types || [];
    return ids.some((id) => domainIds.has(id));
  });
}
